import Sortable from "sortablejs";

import { loadPrefs, savePrefs } from "./candidate_column_prefs";
import { columnWidths, setColumnWidths } from "./candidate_column_widths";

const GRID_SELECTOR = "table[data-candidates-grid]";

// Fired on the table after any layout change, for the Phone Dashboard's Hide menu.
export const COLUMNS_CHANGE_EVENT = "candidate-columns:change";

let delegated = false;

const headRow = (table: HTMLTableElement): HTMLTableRowElement | null =>
  table.tHead?.rows[0] || null;

function currentOrder(table: HTMLTableElement): string[] {
  const row = headRow(table);
  if (!row) return [];
  return Array.from(row.querySelectorAll<HTMLElement>(":scope > th[data-col]")).map(
    (th) => th.dataset.col || ""
  );
}

// Recorded on first sight, before any reorder. A Turbo restore brings the
// attribute back along with the already-reordered DOM, so never re-measure.
function defaultOrder(table: HTMLTableElement): string[] {
  if (!table.dataset.defaultColumnOrder) {
    table.dataset.defaultColumnOrder = JSON.stringify(currentOrder(table));
  }
  try {
    return JSON.parse(table.dataset.defaultColumnOrder);
  } catch (_e) {
    return currentOrder(table);
  }
}

// Hidden columns are one attribute on the table; CSS hides every matching cell
// (_phone_dashboard.scss), so rows arriving from the fragment cache need nothing.
export function hiddenColumns(table: HTMLTableElement): string[] {
  return (table.dataset.hide || "").split(/\s+/).filter(Boolean);
}

function setHiddenColumns(table: HTMLTableElement, keys: string[]): void {
  table.dataset.hide = Array.from(new Set(keys)).join(" ");
}

// Saved keys first, in their saved order. A column the save does not know about
// (added after it was written) goes back in right after its default predecessor.
export function resolveColumnOrder(present: string[], saved: string[]): string[] {
  const out = saved.filter((key, i) => present.includes(key) && saved.indexOf(key) === i);
  present.forEach((key, i) => {
    if (out.includes(key)) return;
    const prev = present.slice(0, i).reverse().find((p) => out.includes(p));
    out.splice(prev ? out.indexOf(prev) + 1 : 0, 0, key);
  });
  return out;
}

// Moves the existing cells rather than re-rendering them, so listeners and
// popovers bound to their contents survive.
function applyOrder(table: HTMLTableElement, order: string[]): void {
  Array.from(table.rows).forEach((tr) => {
    const cells = new Map<string, HTMLElement>();
    tr.querySelectorAll<HTMLElement>(":scope > [data-col]").forEach((cell) => {
      cells.set(cell.dataset.col || "", cell);
    });
    order.forEach((key) => {
      const cell = cells.get(key);
      if (cell) tr.appendChild(cell);
    });
  });
}

function isCustom(table: HTMLTableElement): boolean {
  return (
    currentOrder(table).join() !== defaultOrder(table).join() ||
    hiddenColumns(table).length > 0 ||
    Object.keys(columnWidths(table)).length > 0
  );
}

function syncResetButtons(table: HTMLTableElement): void {
  const custom = isCustom(table);
  document.querySelectorAll<HTMLElement>("[data-columns-reset]").forEach((btn) => {
    btn.hidden = !custom;
  });
}

// The header now holds the order the viewer wants: store it (or forget it when
// it is back to the default), bring the body rows in line, and tell the menu.
export function commit(table: HTMLTableElement): void {
  const order = currentOrder(table);
  savePrefs(table, {
    order: order.join() === defaultOrder(table).join() ? null : order,
    hidden: hiddenColumns(table),
    widths: columnWidths(table),
  });
  applyOrder(table, order);
  syncResetButtons(table);
  table.dispatchEvent(new CustomEvent(COLUMNS_CHANGE_EVENT, { bubbles: true }));
}

export function setColumnHidden(table: HTMLTableElement, key: string, hide: boolean): void {
  const hidden = hiddenColumns(table).filter((k) => k !== key);
  if (hide) hidden.push(key);
  setHiddenColumns(table, hidden);
  commit(table);
}

export function showAllColumns(table: HTMLTableElement): void {
  setHiddenColumns(table, []);
  commit(table);
}

function clearDragHighlight(table: HTMLTableElement): void {
  table.classList.remove("is-col-dragging");
  table.querySelectorAll(".col-hl").forEach((cell) => cell.classList.remove("col-hl"));
}

function bindSortable(table: HTMLTableElement): void {
  const row = headRow(table);
  if (!row || Sortable.get(row)) return;

  Sortable.create(row, {
    draggable: "th[data-col]",
    handle: "[data-col-grip]",
    direction: "horizontal",
    animation: 150,
    ghostClass: "col-ghost",
    onStart: (evt) => {
      const key = (evt.item as HTMLElement).dataset.col;
      table.classList.add("is-col-dragging");
      table.querySelectorAll(`td[data-col="${key}"]`).forEach((td) => td.classList.add("col-hl"));
    },
    onEnd: () => {
      clearDragHighlight(table);
      commit(table);
    },
  });
}

// One step left or right among the visible movable columns: a hidden neighbour is
// skipped, so every move shows. Pinned columns have no `data-col`, so a column never
// moves past Name.
function moveColumn(th: HTMLElement, direction: "left" | "right"): boolean {
  const table = th.closest<HTMLTableElement>(GRID_SELECTOR);
  if (!table) return false;
  const hidden = hiddenColumns(table);

  let sibling = (direction === "left" ? th.previousElementSibling : th.nextElementSibling) as HTMLElement | null;
  while (sibling?.dataset.col && hidden.includes(sibling.dataset.col)) {
    sibling = (direction === "left" ? sibling.previousElementSibling : sibling.nextElementSibling) as HTMLElement | null;
  }
  if (!sibling?.dataset.col) return false;

  if (direction === "left") sibling.before(th);
  else sibling.after(th);
  commit(table);
  return true;
}

// Keyboard: arrow keys on a focused grip move its column one step.
function onKeydown(event: KeyboardEvent): void {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  const grip = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-col-grip]");
  const th = grip?.closest<HTMLElement>("th[data-col]");
  if (!grip || !th) return;

  event.preventDefault();
  if (moveColumn(th, event.key === "ArrowLeft" ? "left" : "right")) grip.focus();
}

function onClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target) return;

  // The ‹ › and hide buttons sit inside the header, next to the sort link.
  const control = target.closest<HTMLElement>("[data-col-move], [data-col-hide]");
  if (control) {
    event.preventDefault();
    event.stopPropagation();
    const th = control.closest<HTMLElement>("th[data-col]");
    const table = th?.closest<HTMLTableElement>(GRID_SELECTOR);
    if (!th || !table) return;

    if (control.dataset.colMove === "left" || control.dataset.colMove === "right") {
      moveColumn(th, control.dataset.colMove);
    } else {
      setColumnHidden(table, th.dataset.col || "", true);
    }
    return;
  }

  // Reset columns: the default order, every column shown again and elastic widths.
  if (!target.closest("[data-columns-reset]")) return;
  document.querySelectorAll<HTMLTableElement>(GRID_SELECTOR).forEach((table) => {
    setHiddenColumns(table, []);
    if (table.dataset.widths !== undefined) setColumnWidths(table, {});
    applyOrder(table, defaultOrder(table));
    commit(table);
  });
}

export function initCandidateColumnOrder(): void {
  if (!delegated) {
    delegated = true;
    document.addEventListener("keydown", onKeydown);
    document.addEventListener("click", onClick);
  }

  document.querySelectorAll<HTMLTableElement>(GRID_SELECTOR).forEach((table) => {
    const defaults = defaultOrder(table);
    const prefs = loadPrefs(table);
    applyOrder(table, prefs.order ? resolveColumnOrder(defaults, prefs.order) : defaults);
    setHiddenColumns(table, prefs.hidden);
    if (table.dataset.widths !== undefined) setColumnWidths(table, prefs.widths || {});
    clearDragHighlight(table);
    syncResetButtons(table);
    bindSortable(table);
    table.dispatchEvent(new CustomEvent(COLUMNS_CHANGE_EVENT, { bubbles: true }));
  });
}
